const BOOKING_RESUME_STORAGE_KEY = 'lamh.pendingBookingResume';
const BOOKING_RESUME_VERSION = 1;
const BOOKING_RESUME_TTL_MS = 24 * 60 * 60 * 1000;

const normalizeEmail = email => (typeof email === 'string' ? email.trim().toLowerCase() : '');

const getLocalStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (error) {
    return null;
  }
};

export const clearBookingResumeDraft = () => {
  const storage = getLocalStorage();
  if (storage) {
    try {
      storage.removeItem(BOOKING_RESUME_STORAGE_KEY);
    } catch (error) {
      // Browsers can deny storage access in restricted privacy modes.
    }
  }
};

export const getBookingResumeDraft = () => {
  const storage = getLocalStorage();
  if (!storage) {
    return null;
  }

  try {
    const rawDraft = storage.getItem(BOOKING_RESUME_STORAGE_KEY);
    const draft = rawDraft ? JSON.parse(rawDraft) : null;
    const expiresAt = draft?.expiresAt ? new Date(draft.expiresAt).getTime() : 0;

    if (
      !draft ||
      draft.version !== BOOKING_RESUME_VERSION ||
      !draft.listingId ||
      !draft.email ||
      !expiresAt ||
      expiresAt <= Date.now()
    ) {
      clearBookingResumeDraft();
      return null;
    }

    return draft;
  } catch (error) {
    clearBookingResumeDraft();
    return null;
  }
};

export const saveBookingResumeDraft = ({
  email,
  listingId,
  listingSlug,
  bookingType,
  userType,
  userId,
  bookingData,
  guestDetails,
  restoreStage = 'reservation-details',
}) => {
  const storage = getLocalStorage();
  if (!storage) {
    return null;
  }

  const normalizedEmail = normalizeEmail(email);
  const parsedGuests = Number.parseInt(bookingData?.numberOfGuests, 10);
  const numberOfGuests = Number.isInteger(parsedGuests) ? parsedGuests : 1;
  if (!normalizedEmail || !listingId) {
    return null;
  }

  const createdAt = new Date();
  const draft = {
    version: BOOKING_RESUME_VERSION,
    createdAt: createdAt.toISOString(),
    expiresAt: new Date(createdAt.getTime() + BOOKING_RESUME_TTL_MS).toISOString(),
    email: normalizedEmail,
    listingId,
    listingSlug,
    bookingType: bookingType === 'conversation' ? 'conversation' : 'reservation',
    userType: userType || null,
    userId: userId || null,
    bookingData: {
      bookingStartTime: bookingData?.bookingStartTime || null,
      bookingEndTime: bookingData?.bookingEndTime || null,
      numberOfGuests,
      // Retain promotional discount code and percentage across guest registration and page reloads
      discountCode: bookingData?.discountCode || null,
      discountPercentage: bookingData?.discountPercentage || null,
    },
    guestDetails: guestDetails || null,
    restoreStage,
  };

  try {
    storage.setItem(BOOKING_RESUME_STORAGE_KEY, JSON.stringify(draft));
    return draft;
  } catch (error) {
    return null;
  }
};

export const bookingResumeMatchesUser = (draft, currentUser) =>
  Boolean(
    draft &&
      currentUser?.attributes?.email &&
      normalizeEmail(draft.email) === normalizeEmail(currentUser.attributes.email)
  );

export const restoreBookingFlowSession = draft => {
  if (typeof window === 'undefined' || !draft) {
    return;
  }

  try {
    window.sessionStorage.setItem('bookingFlowType', draft.bookingType || 'reservation');

    if (draft.userType) {
      window.sessionStorage.setItem('bookingFlowUserType', draft.userType);
    } else {
      window.sessionStorage.removeItem('bookingFlowUserType');
    }

    if (draft.userId) {
      window.sessionStorage.setItem('bookingFlowUserId', draft.userId);
    } else {
      window.sessionStorage.removeItem('bookingFlowUserId');
    }
  } catch (error) {
    // The route state still restores the flow when session storage is unavailable.
  }
};

/**
 * Clears cached reservation contact / bookingData draft from window.sessionStorage
 * so completed or fresh bookings never retain stale discount codes or user inputs.
 *
 * @param {string} [listingId] - Optional listing UUID to clear specifically
 */
export const clearReservationContactSession = listingId => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (listingId) {
      const idStr = typeof listingId === 'object' && listingId?.uuid ? listingId.uuid : String(listingId);
      window.sessionStorage.removeItem(`lamh.reservationContact.${idStr}`);
    }
    window.sessionStorage.removeItem('lamh.reservationContact');

    // Also remove any session storage keys matching lamh.reservationContact
    const keysToRemove = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key && key.startsWith('lamh.reservationContact')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => window.sessionStorage.removeItem(k));
  } catch (error) {
    // Ignore storage access errors in private/restricted browsing
  }
};
